/**
 * The one create-ticket flow a conversation offers, wherever the button sits.
 *
 * The conversation header's quick action and the Details panel's Tickets
 * section both open the same `CaseCreateDialog` through this, and both submits
 * run the same escalation: a POST to the conversation's own
 * `support_case_links` bridge, which opens the case and attaches it as the
 * thread's primary customer link. The successful write announces itself on the
 * bus so the panel's list refetches no matter which surface did the creating.
 */
import { computed, ref, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { useSupportCasesStore } from 'dashboard/stores/supportCases';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// The prefill every surface uses: a ticket opened from a conversation takes
// the thread's subject as its natural title, and an agent can still edit it.
export const conversationCaseTitle = chat =>
  chat?.subject || chat?.additional_attributes?.mail_subject || '';

// Who asked, when a ticket is opened from a thread: the conversation's own
// contact. The agent picks nobody — the thread already names the customer, and
// the server derives the same contact on the escalation, so this is what the
// dialog states rather than a second source of truth.
export const conversationRequester = chat => {
  const sender = chat?.meta?.sender;
  if (!sender?.id) return null;
  return { id: sender.id, name: sender.name || '' };
};

export const useCaseCreateDialog = conversationId => {
  const { t } = useI18n();
  const casesStore = useSupportCasesStore();

  const dialogRef = ref(null);
  const isSubmitting = ref(false);

  const caseTypes = computed(() => casesStore.caseTypeOptions);

  // The catalogue is fetched when the dialog is actually asked for: the
  // surfaces carrying this flow render on every conversation, and most of
  // them never open a ticket.
  const openDialog = async () => {
    await casesStore.fetchCaseTypes();
    dialogRef.value?.open();
  };

  // The dialog's whole answer goes to the bridge — the type, the title, the
  // priority, the first message and who asked. Naming the fields here would be
  // a second copy of the intake shape; the bridge's own client is the one place
  // that maps them onto the request body.
  const onSubmit = async payload => {
    isSubmitting.value = true;
    try {
      await ConversationApi.createSupportCaseLink(
        toValue(conversationId),
        payload
      );
      dialogRef.value?.close();
      emitter.emit(BUS_EVENTS.SUPPORT_CASE_LINKS_UPDATED);
      useAlert(t('SUPPORT_CASES.CONVERSATION.CREATE_SUCCESS'));
    } catch (error) {
      // A refusal from the bridge states itself: the commands answer 422 with
      // an i18n'd sentence of their own, which is more use to an agent than
      // any line written here. The stated fallback is for the failures that
      // carry no sentence at all: a dropped request, a 500.
      useAlert(
        error?.response?.data?.message ||
          t('SUPPORT_CASES.CONVERSATION.CREATE_ERROR')
      );
    } finally {
      isSubmitting.value = false;
    }
  };

  return { dialogRef, caseTypes, isSubmitting, openDialog, onSubmit };
};
